import { useState, useEffect, FormEvent } from "react"
import { useSession } from "../contexts/SessionContext"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Invitation, ApiResponse } from "@prescreen/lib/types"
import { apiFetch } from "@prescreen/lib/api-client"

interface InvitationWithUserInfo extends Invitation {
  userExists?: boolean
}

interface InvitationsTabProps {
  onInvitationCreated?: () => void
}

export default function InvitationsTab({ onInvitationCreated }: InvitationsTabProps) {
  const { user: session } = useSession()
  const [invitations, setInvitations] = useState<InvitationWithUserInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<"administrator" | "integrator">("integrator")
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "used" | "expired">("all")

  // Check if user is administrator
  const isAdmin = session?.role === "administrator"

  useEffect(() => {
    if (isAdmin) {
      fetchInvitations()
    }
  }, [isAdmin, statusFilter])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const response = await apiFetch(`/api/admin/invitations?status=${statusFilter}`)
      const data: ApiResponse<{ invitations: InvitationWithUserInfo[] }> = await response.json()
      
      if (data.success && data.data) {
        setInvitations(data.data.invitations)
      }
    } catch (err) {
      console.error("Error fetching invitations:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateInvitation = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCreating(true)
    setError(null)

    try {
      const response = await apiFetch("/api/admin/invitations", {
        method: "POST",
        body: JSON.stringify({ email, role }),
      })

      const data: ApiResponse<{ invitation: Invitation }> = await response.json()

      if (data.success) {
        setEmail("")
        setRole("integrator")
        fetchInvitations()
        onInvitationCreated?.()
      } else {
        setError(data.error || "Failed to create invitation")
      }
    } catch (err) {
      setError("An unexpected error occurred")
    } finally {
      setCreating(false)
    }
  }

  const handleRevokeInvitation = async (id: string) => {
    if (!confirm("Are you sure you want to revoke this invitation?")) {
      return
    }

    try {
      const response = await apiFetch(`/api/admin/invitations/${id}`, {
        method: "DELETE",
      })

      const data: ApiResponse = await response.json()

      if (data.success) {
        fetchInvitations()
      } else {
        alert(data.error || "Failed to revoke invitation")
      }
    } catch (err) {
      alert("An unexpected error occurred")
    }
  }

  const getInvitationStatus = (invitation: InvitationWithUserInfo): "pending" | "used" | "expired" => {
    // Consider invitation as "used" if it has been accepted OR if a user exists with that email
    if (invitation.usedAt || invitation.userExists) return "used"
    if (new Date(invitation.expiresAt) < new Date()) return "expired"
    return "pending"
  }

  const canRevokeInvitation = (invitation: InvitationWithUserInfo): boolean => {
    // Cannot revoke if invitation has been used or if user has logged in
    return !invitation.usedAt && !invitation.userExists
  }

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-600">You must be an administrator to manage invitations.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Create Invitation Form */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Invitation</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateInvitation} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={creating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as "administrator" | "integrator")}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={creating}
                >
                  <option value="integrator">Integrator</option>
                  <option value="administrator">Administrator</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Button type="submit" disabled={creating || !email}>
              {creating ? "Creating..." : "Send Invitation"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Invitations List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Invitations</CardTitle>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("all")}
              >
                All
              </Button>
              <Button
                variant={statusFilter === "pending" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("pending")}
              >
                Pending
              </Button>
              <Button
                variant={statusFilter === "used" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("used")}
              >
                Used
              </Button>
              <Button
                variant={statusFilter === "expired" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("expired")}
              >
                Expired
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-600">Loading invitations...</p>
          ) : invitations.length === 0 ? (
            <p className="text-gray-600">No invitations found.</p>
          ) : (
            <div className="space-y-4">
              {invitations.map((invitation) => {
                const status = getInvitationStatus(invitation)
                const statusColors = {
                  pending: "bg-blue-100 text-blue-800 border-blue-200",
                  used: "bg-green-100 text-green-800 border-green-200",
                  expired: "bg-gray-100 text-gray-800 border-gray-200",
                }

                return (
                  <div
                    key={invitation._id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium">{invitation.email}</span>
                        <Badge className={`text-xs border ${statusColors[status]}`}>
                          {status}
                        </Badge>
                        <Badge
                          className={`text-xs border ${
                            invitation.role === "administrator"
                              ? "bg-purple-100 text-purple-800 border-purple-200"
                              : "bg-blue-100 text-blue-800 border-blue-200"
                          }`}
                        >
                          {invitation.role}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>Created: {formatDate(invitation.createdAt)}</p>
                        <p>Expires: {formatDate(invitation.expiresAt)}</p>
                        {invitation.usedAt && (
                          <p>Used: {formatDate(invitation.usedAt)}</p>
                        )}
                      </div>
                    </div>
                    {canRevokeInvitation(invitation) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevokeInvitation(invitation._id!)}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

